package ui.hardware.detail


import androidx.compose.animation.*
import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.*
import androidx.compose.foundation.shape.*
import androidx.compose.foundation.text.selection.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.*
import androidx.compose.ui.draw.*
import androidx.compose.ui.graphics.*
import androidx.compose.ui.layout.*
import androidx.compose.ui.platform.*
import androidx.compose.ui.text.style.*
import androidx.compose.ui.unit.*
import api.*
import com.mohamedrejeb.richeditor.model.*
import com.mohamedrejeb.richeditor.ui.material3.*
import org.jetbrains.compose.resources.*
import provider.*
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.*
import tk.mallumo.utils.*
import tools.*
import ui.resource.*
import ui.widget.*


data class HardwareDetailArgs(var hardwareId: Long = -1)

@ComposableNavNode(HardwareDetailArgs::class)
@Composable
fun HardwareDetailUI() {
    with(HardwareDetailScope.rememberScope()) {
        when (val state = vm.state) {
            is DataState.Error -> ErrUI()
            is DataState.Idle,
            is DataState.Loading -> LoadingUI()

            is DataState.Result -> Content(state.nnvl)
        }
    }
}


@Composable
fun HardwareDetailScope.Content(nnvl: HardwareDetailScope.Wrapper) {

    GlobalObjectShare.Consumer(nnvl.image.fieldName)

    val toolbarActions = remember {
        buildList {
            if (nnvl.canEdit) {
                add(ToolbarAction.save(nnvl.change) { action(HardwareDetailVM.Action.Save) })
            }
        }
    }

    val title = Res.string.hardware_detail__title.rememberString()
    val toolbarTitle = remember(title, args.hardwareId, nnvl.data) {
        title to (("(${args.hardwareId})".takeIf { args.hardwareId > 0 }) ?: "")
    }

    val activeLang = remember {
        mutableStateOf<LanguageType>(LanguageType.English)
    }
    Column(
        modifier = Modifier.fillMaxSize(),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        Toolbar(
            nav = nav,
            titlePrefix = toolbarTitle.first,
            titleSuffix = toolbarTitle.second,
            actions = toolbarActions
        )
        LazyColumn(
            modifier = Modifier.fillMaxWidth().weight(1F),
            contentPadding = PaddingValues(8.dp)
        ) {
            item {
                Top(nnvl)
            }
            item {
                if (nnvl.canEdit) {
                    BottomEditable(Modifier.fillMaxWidth(), nnvl, activeLang)
                } else {
                    BottomPreview(Modifier.fillMaxWidth(), nnvl, LocalLocalization.current.language)
                }
            }
        }

        if (nnvl.canEdit) LangFooter(activeLang)
    }
}

@Composable
@OptIn(ExperimentalMaterial3Api::class)
private fun LangFooter(activeLang: MutableState<LanguageType>) {
    Box(Modifier.fillMaxWidth()) {
        SecondaryTabRow(
            selectedTabIndex = LanguageType.allSorted.indexOf(activeLang.value),
            containerColor = MaterialTheme.colorScheme.tertiaryContainer,
        ) {
            LanguageType.allSorted.forEach { lang ->
                Tab(
                    selected = lang == activeLang.value,
                    onClick = { activeLang.value = lang },
                    text = { Text(lang.title) }
                )
            }
        }
    }
}

@Composable
fun HardwareDetailScope.Top(nnvl: HardwareDetailScope.Wrapper) {
    fun openResourcePicker() {
        GlobalObjectShare.launchImagePicker(
            key = nnvl.image.fieldName,
            nav = nav,
            args = ResourceListingArgs(
                companyId = login.company.id,
                restrictionType = ResourceType.IMAGE.mask,
            )
        ) {
            it.firstOrNull()?.apply {
                nnvl.image.set(id)
            }
        }
    }

    TextField(
        label = {
            Text(Res.string.hardware_detail__name.rememberString())
        },
        value = nnvl.name.text,
        readOnly = !nnvl.canEdit,
        modifier = Modifier.width(512.dp),
        onValueChange = {
            nnvl.name.set(it)
        }
    )
    Space(8.dp)
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        LazyImage(
            id = nnvl.image.state,
            contentScale = ContentScale.Fit,
            alphaBackground = true,
            modifier = Modifier.size(192.dp)
                .clickable(nnvl.canEdit) { openResourcePicker() }
        )
        Column(
            verticalArrangement = Arrangement.spacedBy(8.dp),
            modifier = Modifier.align(Alignment.CenterVertically)
        ) {
            Row(
                modifier = Modifier.clickable { nnvl.canApps.set(!nnvl.canApps.state) }
                    .padding(horizontal = 8.dp),
                horizontalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                IconicSwitch(
                    checked = nnvl.canApps.state,
                    enabled = nnvl.canEdit,
                    onCheckedChange = { nnvl.canApps.set(it) },
                )
                Text(
                    text = Res.string.hardware_detail__cap_apps.rememberString(),
                    modifier = Modifier.align(Alignment.CenterVertically)
                )
            }
            Row(
                modifier = Modifier.clickable { nnvl.canFHD.set(!nnvl.canFHD.state) }
                    .padding(horizontal = 8.dp),
                horizontalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                IconicSwitch(
                    checked = nnvl.canFHD.state,
                    enabled = nnvl.canEdit,
                    onCheckedChange = { nnvl.canFHD.set(it) },
                )
                Text(
                    text = Res.string.hardware_detail__cap_fhd.rememberString(),
                    modifier = Modifier.align(Alignment.CenterVertically)
                )
            }
            Row(
                modifier = Modifier.clickable { nnvl.canUHD.set(!nnvl.canUHD.state) }
                    .padding(horizontal = 8.dp),
                horizontalArrangement = Arrangement.spacedBy(16.dp)
            ) {
                IconicSwitch(
                    checked = nnvl.canUHD.state,
                    enabled = nnvl.canEdit,
                    onCheckedChange = { nnvl.canUHD.set(it) },
                )
                Text(
                    text = Res.string.hardware_detail__cap_uhd.rememberString(),
                    modifier = Modifier.align(Alignment.CenterVertically)
                )
            }
        }
    }
}

private enum class EditMode(val label: StringResource) {
    PREVIEW(Res.string.hardware_detail__filter_preview),
    CODE_PREVIEW(Res.string.hardware_detail__filter_sources_preview),
    CODE(Res.string.hardware_detail__filter_sources),
}

@Composable
private fun HardwareDetailScope.BottomEditable(
    modifier: Modifier,
    nnvl: HardwareDetailScope.Wrapper,
    activeLang: State<LanguageType>
) {

    var editMode by remember {
        mutableStateOf(EditMode.PREVIEW)
    }
    Crossfade(
        targetState = activeLang.value,
        modifier = modifier.padding(top = 8.dp)
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            ShortDescription(nnvl, it)
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                EditMode.entries.forEach { entry ->
                    FilterChip(
                        selected = editMode == entry,
                        onClick = { editMode = entry },
                        label = { Text(entry.label.rememberString()) })
                }

            }
            nnvl.description[it]?.also { sub ->
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    when (editMode) {
                        EditMode.CODE_PREVIEW -> {
                            LongDescriptionCode(sub, Modifier.weight(1F))
                            LongDescriptionPreview(sub, Modifier.weight(1F))
                        }

                        EditMode.CODE -> LongDescriptionCode(sub, Modifier.weight(1F))
                        EditMode.PREVIEW -> LongDescriptionPreview(sub, Modifier.weight(1F))
                    }

                }
            }
        }
    }
}

@Composable
fun LongDescriptionCode(sub: EFieldTextString, modifier: Modifier) {
    OutlinedTextField(
        modifier = modifier,
        value = sub.text,
        minLines = 10,
        onValueChange = {
            sub.set(it)
        })
}

@Composable
fun HardwareDetailScope.LongDescriptionPreview(sub: EFieldTextString, modifier: Modifier) {
    val richTextState = rememberRichTextState()
    LaunchedEffect(sub.state) {
        richTextState.setMarkdown(sub.text.text)
    }
    LaunchedEffect(Unit) {
        richTextState.config.linkColor = theme.primaryDark
        richTextState.config.linkTextDecoration = TextDecoration.None
        richTextState.config.codeSpanColor = Color(0xFFd7882d)
        richTextState.config.codeSpanBackgroundColor = Color.Transparent
        richTextState.config.codeSpanStrokeColor = Color(0xFF494b4d)
        richTextState.config.unorderedListIndent = 40
        richTextState.config.orderedListIndent = 50
    }
    CompositionLocalProvider(LocalUriHandler provides uriHandler) {
        SelectionContainer(modifier) {
            RichText(
                modifier = Modifier
                    .clip(RoundedCornerShape(4.dp))
                    .background(Color.White, RoundedCornerShape(4.dp))
                    .padding(1.dp)
                    .background(MaterialTheme.colorScheme.background, RoundedCornerShape(4.dp))
                    .padding(8.dp),
                state = richTextState
            )
        }
    }
}

@Composable
fun HardwareDetailScope.BottomPreview(
    modifier: Modifier,
    nnvl: HardwareDetailScope.Wrapper,
    language: LanguageType
) {
    Column(
        verticalArrangement = Arrangement.spacedBy(8.dp),
        modifier = modifier.padding(top = 8.dp)
    ) {
        ShortDescription(nnvl, language)
        nnvl.description[language]
            ?.takeIf { it.state.trim().isNotEmpty() }
            ?.also { sub ->
                LongDescriptionPreview(sub, Modifier.defaultMinSize(512.dp))
            }
    }
}

@Composable
fun HardwareDetailScope.ShortDescription(
    nnvl: HardwareDetailScope.Wrapper,
    lang: LanguageType
) {
    nnvl.subtitles[lang]?.also { sub ->
        TextField(
            label = { Text(Res.string.hardware_detail__desc_short.rememberString()) },
            value = sub.text,
            readOnly = !nnvl.canEdit,
            modifier = Modifier.width(512.dp),
            onValueChange = {
                sub.set(it)
            }
        )
    }
}
